import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";

import SEO from "../components/SEO";
import PublicSiteHeader from "../components/PublicSiteHeader";
import AppFooter from "../components/AppFooter";

import logo from "../assets/logo_basic.png";

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [selectedCategoryId, setSelectedCategoryId] =
    useState("");

  const [selectedCategoryName, setSelectedCategoryName] =
    useState("All");

  const [searchText, setSearchText] =
    useState("");

  const [loading, setLoading] =
    useState(true);

  // =====================================================
  // LOAD BLOG CATEGORIES
  // =====================================================
  const loadCategories = async () => {
    try {
      const {
        data,
        error,
      } = await supabase
        .from("blog_categories")
        .select("*")
        .eq("is_active", true)
        .order("name", {
          ascending: true,
        });

      if (error) {
        console.error(
          "Blog category load error:",
          error
        );

        setCategories([]);
        return;
      }

      setCategories(
        Array.isArray(data)
          ? data
          : []
      );
    } catch (error) {
      console.error(
        "Blog category loading failed:",
        error
      );

      setCategories([]);
    }
  };

  // =====================================================
  // LOAD BLOG POSTS
  // =====================================================
  const loadPosts = async (
    categoryId = ""
  ) => {
    try {
      setLoading(true);

      let query = supabase
        .from("blog_posts")
        .select(`
          *,
          blog_categories (
            id,
            name,
            slug
          )
        `)
        .eq(
          "status",
          "published"
        )
        .order(
          "published_at",
          {
            ascending: false,
          }
        );

      if (categoryId) {
        query = query.eq(
          "category_id",
          categoryId
        );
      }

      const {
        data,
        error,
      } = await query;

      if (error) {
        console.error(
          "Blog load error:",
          error
        );

        setPosts([]);
        return;
      }

      setPosts(
        Array.isArray(data)
          ? data
          : []
      );
    } catch (error) {
      console.error(
        "Blog loading failed:",
        error
      );

      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // INITIAL LOAD
  // =====================================================
  useEffect(() => {
    const initializeBlogPage =
      async () => {
        await Promise.all([
          loadCategories(),
          loadPosts(""),
        ]);
      };

    initializeBlogPage();
  }, []);

  // =====================================================
  // CATEGORY CLICK
  // =====================================================
  const handleCategoryClick = (
    categoryId,
    categoryName
  ) => {
    setSelectedCategoryId(
      categoryId
    );

    setSelectedCategoryName(
      categoryName
    );

    loadPosts(
      categoryId
    );
  };

  // =====================================================
  // SAFE ARRAYS
  // =====================================================
  const safeCategories =
    Array.isArray(categories)
      ? categories
      : [];

  // =====================================================
  // SEARCH FILTER
  // =====================================================
  const filteredPosts =
    useMemo(() => {
      const sourcePosts =
        Array.isArray(posts)
          ? posts
          : [];

      const queryText =
        searchText
          .trim()
          .toLowerCase();

      if (!queryText) {
        return sourcePosts;
      }

      return sourcePosts.filter(
        (post) => {
          const title =
            String(
              post?.title || ""
            ).toLowerCase();

          const excerpt =
            String(
              post?.excerpt || ""
            ).toLowerCase();

          const content =
            String(
              post?.content || ""
            ).toLowerCase();

          const categoryName =
            String(
              post
                ?.blog_categories
                ?.name || ""
            ).toLowerCase();

          return (
            title.includes(
              queryText
            ) ||
            excerpt.includes(
              queryText
            ) ||
            content.includes(
              queryText
            ) ||
            categoryName.includes(
              queryText
            )
          );
        }
      );
    }, [
      posts,
      searchText,
    ]);

  // =====================================================
  // FEATURED POSTS
  // =====================================================
  const featuredPosts =
    Array.isArray(
      filteredPosts
    )
      ? filteredPosts
          .filter(
            (post) =>
              post?.is_featured ===
              true
          )
          .slice(
            0,
            3
          )
      : [];

  // =====================================================
  // LATEST POSTS
  // =====================================================
  const latestPosts =
    Array.isArray(
      filteredPosts
    )
      ? filteredPosts.filter(
          (post) =>
            post?.is_featured !==
            true
        )
      : [];

  // =====================================================
  // BLOG CARD
  // =====================================================
  const BlogCard = ({
    post,
  }) => {
    if (!post) {
      return null;
    }

    return (
      <article className="rounded-3xl border border-[#EB6AB5]/25 bg-white/5 overflow-hidden hover:-translate-y-1 transition">
        {post.cover_image_url && (
          <img
            src={
              post.cover_image_url
            }
            alt={
              post.title ||
              "Blog article"
            }
            className="w-full h-52 object-cover"
            loading="lazy"
          />
        )}

        <div className="p-6">
          <p className="text-[#EB6AB5] text-xs font-bold uppercase tracking-widest mb-3">
            {post
              .blog_categories
              ?.name ||
              "Career Strategy"}
          </p>

          <h2 className="text-2xl font-bold mb-3">
            {post.title}
          </h2>

          <p className="text-white/70 mb-5">
            {post.excerpt}
          </p>

          {post.slug && (
            <Link
              to={`/blog/${post.slug}`}
              className="text-[#EB6AB5] font-semibold underline underline-offset-4"
            >
              Read Article →
            </Link>
          )}
        </div>
      </article>
    );
  };

  return (
    <div className="min-h-screen bg-[#0F0A24] text-white">
      {/* =====================================================
          SEO
      ===================================================== */}
      <SEO
        title="Career Intelligence Blog"
        description="Read expert articles on AI careers, career pivots, financial runway planning, job market intelligence and future-proof skills."
        canonical="https://www.reignitespirit.com/blog"
        image="https://www.reignitespirit.com/og-blog.jpg"
        keywords="
          AI Careers,
          Career Pivot,
          Career Intelligence,
          Financial Runway,
          Future Jobs,
          AI Proof Careers,
          Reignite Spirit
        "
      />

      {/* =====================================================
          SHARED PUBLIC HEADER

          IMPORTANT:
          Register + Get My Audit behavior now comes from
          PublicSiteHeader.jsx.

          Both go to /landing#pricing.
      ===================================================== */}
      <PublicSiteHeader
        active="blog"
      />

      {/* =====================================================
          BLOG CONTENT
      ===================================================== */}
      <main className="px-6 py-16">
        <section className="max-w-7xl mx-auto">

          {/* =================================================
              HERO / INTRO
          ================================================= */}
          <div className="text-center mb-10">
            <p className="text-[#EB6AB5] font-bold tracking-[0.25em] uppercase text-sm">
              Career Intelligence Hub
            </p>

            <h1 className="text-4xl md:text-6xl font-extrabold mt-4">
              Insights for Smarter
              Career Moves
            </h1>

            <p className="text-white/70 max-w-3xl mx-auto mt-5 text-lg">
              Practical guides on
              career pivots,
              financial runway
              planning, AI-proof
              careers, and local job
              market intelligence.
            </p>

            {/* =============================================
                ARTICLE CONTRIBUTION MESSAGE
            ============================================= */}
            <p className="text-white/80 max-w-3xl mx-auto mt-4 text-base md:text-lg leading-relaxed">
              Your experience could
              inspire someone’s next
              career move. You can
              also share your insights
              through an article under
              any of the subheadings
              below.
            </p>

            <div className="mt-8 flex justify-center">
              <Link
                to="/submit-article"
                className="px-7 py-3 rounded-full bg-[#EB6AB5] text-[#0F0A24] font-bold hover:bg-pink-400 transition"
              >
                Submit Your Article →
              </Link>
            </div>
          </div>

          {/* =================================================
              SEARCH
          ================================================= */}
          <div className="max-w-2xl mx-auto mb-8">
            <input
              type="text"
              value={searchText}
              onChange={(
                event
              ) =>
                setSearchText(
                  event.target
                    .value
                )
              }
              placeholder="Search articles..."
              className="w-full px-5 py-4 rounded-2xl bg-white/5 border border-[#EB6AB5]/30 text-white outline-none focus:border-[#EB6AB5] transition"
            />
          </div>

          {/* =================================================
              CATEGORY FILTERS
          ================================================= */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            <button
              type="button"
              onClick={() =>
                handleCategoryClick(
                  "",
                  "All"
                )
              }
              className={`px-5 py-2 rounded-full border transition ${
                selectedCategoryId ===
                ""
                  ? "bg-[#EB6AB5] text-[#0F0A24] border-[#EB6AB5]"
                  : "bg-white/5 text-white border-white/10 hover:border-[#EB6AB5]"
              }`}
            >
              All
            </button>

            {safeCategories.map(
              (category) => (
                <button
                  type="button"
                  key={
                    category.id
                  }
                  onClick={() =>
                    handleCategoryClick(
                      category.id,
                      category.name
                    )
                  }
                  className={`px-5 py-2 rounded-full border transition ${
                    selectedCategoryId ===
                    category.id
                      ? "bg-[#EB6AB5] text-[#0F0A24] border-[#EB6AB5]"
                      : "bg-white/5 text-white border-white/10 hover:border-[#EB6AB5]"
                  }`}
                >
                  {
                    category.name
                  }
                </button>
              )
            )}
          </div>

          <p className="text-center text-white/60 mb-10">
            Showing category:{" "}
            <span className="text-[#EB6AB5] font-semibold">
              {
                selectedCategoryName
              }
            </span>
          </p>

          {/* =================================================
              BLOG RESULTS
          ================================================= */}
          {loading ? (
            <p className="text-center text-white/60 py-10">
              Loading blog posts...
            </p>
          ) : filteredPosts
              .length === 0 ? (
            <p className="text-center text-white/60 py-10">
              No blog posts found.
            </p>
          ) : (
            <>
              {/* =============================================
                  FEATURED POSTS
              ============================================= */}
              {featuredPosts
                .length > 0 && (
                <section className="mb-16">
                  <div className="mb-6">
                    <p className="text-[#EB6AB5] font-bold uppercase tracking-widest text-sm">
                      ★★★★★ Featured
                      Articles
                    </p>

                    <h2 className="text-3xl md:text-4xl font-bold mt-2">
                      Editor’s Picks
                    </h2>
                  </div>

                  <div className="grid md:grid-cols-3 gap-8">
                    {featuredPosts.map(
                      (post) => (
                        <BlogCard
                          key={
                            post.id ||
                            post.slug
                          }
                          post={
                            post
                          }
                        />
                      )
                    )}
                  </div>
                </section>
              )}

              {/* =============================================
                  LATEST POSTS
              ============================================= */}
              <section>
                <div className="mb-6">
                  <p className="text-[#EB6AB5] font-bold uppercase tracking-widest text-sm">
                    Latest Articles
                  </p>

                  <h2 className="text-3xl md:text-4xl font-bold mt-2">
                    New Career
                    Intelligence
                  </h2>
                </div>

                {latestPosts
                  .length ===
                0 ? (
                  <p className="text-white/60">
                    No latest
                    articles
                    available.
                  </p>
                ) : (
                  <div className="grid md:grid-cols-3 gap-8">
                    {latestPosts.map(
                      (post) => (
                        <BlogCard
                          key={
                            post.id ||
                            post.slug
                          }
                          post={
                            post
                          }
                        />
                      )
                    )}
                  </div>
                )}
              </section>
            </>
          )}

          {/* =================================================
              CAREER PLANNING RESOURCES
          ================================================= */}
          <section
            className="mt-16 rounded-3xl border border-white/10 bg-white/5 p-7 md:p-9"
            aria-labelledby="blog-planning-resources"
          >
            <h2
              id="blog-planning-resources"
              className="text-2xl md:text-3xl font-bold text-white"
            >
              Career planning
              resources
            </h2>

            <p className="text-white/65 mt-3 max-w-3xl leading-relaxed">
              Explore the reports,
              methodology and
              supporting information
              behind Reignite Spirit
              career intelligence.
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-7">
              {/* SNAPSHOT */}
              <Link
                to="/snapshot"
                className="rounded-xl border border-white/10 p-5 hover:border-[#EB6AB5] transition"
              >
                <h3 className="font-bold text-white">
                  Snapshot Report
                </h3>

                <p className="text-sm text-white/60 mt-2">
                  Start with a
                  focused career-risk
                  assessment.
                </p>
              </Link>

              {/* METHODOLOGY */}
              <Link
                to="/methodology"
                className="rounded-xl border border-white/10 p-5 hover:border-[#EB6AB5] transition"
              >
                <h3 className="font-bold text-white">
                  Methodology
                </h3>

                <p className="text-sm text-white/60 mt-2">
                  Review how career
                  intelligence is
                  produced.
                </p>
              </Link>

              {/* DATA SOURCES */}
              <Link
                to="/data-sources"
                className="rounded-xl border border-white/10 p-5 hover:border-[#EB6AB5] transition"
              >
                <h3 className="font-bold text-white">
                  Data Sources
                </h3>

                <p className="text-sm text-white/60 mt-2">
                  Understand the
                  information
                  considered by the
                  platform.
                </p>
              </Link>

              {/* FAQ */}
              <Link
                to="/faq"
                className="rounded-xl border border-white/10 p-5 hover:border-[#EB6AB5] transition"
              >
                <h3 className="font-bold text-white">
                  FAQ
                </h3>

                <p className="text-sm text-white/60 mt-2">
                  Read answers about
                  reports and
                  assessments.
                </p>
              </Link>
            </div>
          </section>
        </section>
      </main>

      {/* =====================================================
          SAME FOOTER AS FAQ
      ===================================================== */}
      <AppFooter
        variant="landing"
        logoSrc={logo}
      />
    </div>
  );
}